<template>
    <div class="w-full">
        <div v-if="buttonState === 'addOrView'" @click="cartButtonClicked"
        class="item-view-secondary-action-btn w-full py-2 rounded-lg items-center justify-center text-white cursor-pointer text-center"
        :style="{ backgroundColor }">
        <span class="text-base font-base">
            {{ item.type === 'lead' ? 'View Product' : addToCartTextTitle }}
        </span>
        </div>
    
        <div v-else-if="buttonState === 'learnOrContact'" class="w-full">
        <div @click="handleLearnOrContact"
            class="item-view-secondary-action-btn bg-primary w-full py-2 rounded items-center justify-center text-white cursor-pointer text-center">
            <span class="text-base font-base">{{ supplierActionLabel() }}</span>
        </div>
        </div>
    
        <div v-else-if="buttonState === 'contactFreshOnly'" class="w-full">
        <div @click="popupActivate"
            class="item-view-secondary-action-btn bg-primary w-full py-2 rounded items-center justify-center text-white cursor-pointer text-center">
            <span class="text-base font-base">Contact Fresh</span>
        </div>
        </div>
    
        <div v-else-if="buttonState === 'outOfStock'" class="w-full">
        <div class="item-view-secondary-action-btn bg-gray w-full py-2 rounded items-center justify-center text-white cursor-pointer text-center">
            <span class="text-base font-base">Out of Stock</span>
        </div>
        </div>
        <vs-popup
            class="holamundo"
            title="Supplier Account Required"
            :active.sync="showPopup"
        >
            <div class="flex flex-no-wrap">
            <div class="w-1/6 flex items-center justify-center">
                <vs-icon
                icon="clear"
                size="medium"
                bg="#f9d6d6"
                color="#b21f1e"
                round
                ></vs-icon>
            </div>
            <div class="w-5/6">
                <ContactFresh />
            </div>
            </div>
            <div class="flex justify-end mt-5">
            <vs-button
                class="mr-4 mb-4"
                @click="showPopup = false"
            >
                I understand
            </vs-button>
            </div>
        </vs-popup>
    </div>
  </template>
  
<script>
import {
  createServerRootMixin,
} from 'vue-instantsearch';
import storeHelper from '../../../globalHelpers/storeHelper';
import { currentUserIsDoctor } from '../../../globalHelpers/userRole';
import { mapActions } from 'vuex';
import { NOTIFICATIONS } from '@/views/components/order-center/constants';
import {
  trackEcommerceEvent,
  ADD_TO_CART_EVENT,
  formatPriceValue,
  getCurrency,
} from '../../../utils/gtag';
import ContactFresh from '../ContactFresh.vue';
import { getSearchClient } from '@/utils/storeAlgoliaSearch';
const searchClient = getSearchClient();

export default {
  name: 'AddToCartButton',
  components: {
    ContactFresh,
  },
  mixins: [
    storeHelper,
    createServerRootMixin({
      searchClient,
      indexName: 'instant_search',
    }),
  ],
  inject: ['getRoleRouteName'],
  data() {
    return {
      addToCartTextTitle: 'Add to Cart',
      showPopup: false,
      orderApprovalFlowEnabled: false,
    };
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    backgroundColor: {
      type: String,
      default: '#0E4333',
    },
  },
  computed: {
    superAdminCanAddToCart() {
      return (this.userTypeAdmin === 'superAdmin') && !!this.canAdd;
    },
    totalItemQuantityIncart() {
      return (itemId) => this.$store.getters['eCommerce/totalItemQuantityIncart'](itemId);
    },
    showAddOrViewButton() {
      return (
        ((this.item.canBuy && this.item.isMatchS4Medication) || this.superAdminCanAddToCart || this.isProductScript(this.item) || this.isProductBundle(this.item)) &&
                this.item.isAvailable
      );
    },
    showLearnOrContactButton() {
      return !this.item.canBuy && !this.superAdminCanAddToCart;
    },
    showContactFreshOnly() {
      return (
        !this.isInCartTest(this.item._id) &&
            this.item.canBuy &&
            !this.superAdminCanAddToCart &&
            !this.item.isMatchS4Medication
      );
    },
    showOutOfStock() {
      return (
        !this.item.isAvailable &&
                this.item.canBuy &&
                this.item.isMatchS4Medication
      );
    },
    isInCartTest() {
      return (itemId) => this.$store.getters['eCommerce/isInCartTest'](itemId);
    },
    buttonState() {
      const item = this.item;

      if (
        ((item.canBuy && item.isMatchS4Medication) || 
            this.superAdminCanAddToCart || 
            this.isProductScript(item) || 
            this.isProductBundle(item)) &&
            item.isAvailable
      ) {
        return 'addOrView';
      }

      if (!item.canBuy && !this.superAdminCanAddToCart) {
        return 'learnOrContact';
      }

      if (
        !this.isInCartTest(item._id) &&
            item.canBuy &&
            !this.superAdminCanAddToCart &&
            !item.isMatchS4Medication
      ) {
        return 'contactFreshOnly';
      }
      if (!item.isAvailable && item.canBuy && item.isMatchS4Medication) {
        return 'outOfStock';
      }

      return null; // fallback
    },

  },
  methods: {
    ...mapActions('ecommerce', ['getHomepageProductsByIds']),
    ...mapActions('ecommerce', ['getCartItemsFromApi']),
    ...mapActions('admin', ['getSettingsByKey']),
    async cartButtonClicked() {
      if (this.item.type !== 'basic' || this.item.tcc || this.item.type === 'bundle' || this.item.type === 'lead' || this.item.type === 'script') {
        this.$router
          .push({
            name: this.getRoleRouteName('ProductDetail'),
            params: {
              id: this.item._id,
            },
          });
        return false;
      }

      const notifyError = (title, text) => {
        this.$vs.notify({
          title,
          text,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
        });
      };

      let isValid = await this.$validator.validateAll('basic');
      if (!isValid) {
        notifyError('Error', 'Validation Failed');
        return false;
      }
      if (this.$store.state.clinicId === null) {
        notifyError('Failed', 'Please Select a clinic.');
        return false;
      }
      if (this.item.canAdd === false) {
        notifyError('Failed', `Selected quantity must not be less than ${this.item.inventory.requiredQuantity}`);
      }
        
      const cartQuantity = this.totalItemQuantityIncart(this.item._id);
      if (this.item.selectedQuantity === 0) {
        notifyError('Failed', 'Quantity cannot be zero.');
      }
      if (this.item.isAvailable !== true) {
        notifyError('Failed', 'Product is not available right now.');
        return false;
      }

      const totalCartAndSelectedQty = (cartQuantity + this.item.selectedQuantity);
      if (this.item.inventory.maxQuantity !== null && this.item.inventory.maxQuantity !== 0) {
        if (this.item.selectedQuantity > this.item.inventory.maxQuantity || totalCartAndSelectedQty > this.item.inventory.maxQuantity) {
          this.$vs.notify({
            title: 'Failed',
            text: 'You\'ve exceeded maximum order amount.',
            color: 'danger',
          });
          return false;
        }
      }
      this.additemInCart(this.item, 'increment');
    },
    additemInCart(data, type) {
      const minQty = Math.max(1, data.inventory.requiredQuantity);
      let item = { data, type, minQty };

      this.$vs.loading();

      item.data.shipping.shippingFee = item.data.supplier.deliveryDetails
        ? item.data.supplier.deliveryDetails.deliveryFee
        : 0;

      this.$store
        .dispatch('eCommerce/additemInCartHomepage', item)
        .then(() => {
          trackEcommerceEvent(ADD_TO_CART_EVENT, {
            currency: getCurrency(),
            value: formatPriceValue(item.data.price),
            items: [
              {
                item_id: item.data._id,
                item_name: item.data.name,
                quantity: minQty,
                price: formatPriceValue(item.data.price),
              },
            ],
          });

          this.getCartItemsFromApi(); 
          this.$vs.loading.close();
          this.$vs.notify(NOTIFICATIONS.PRODUCT_ADDED(this.orderApprovalFlowEnabled));
        })
        .catch((err) => {
          this.$vs.loading.close();
          const supplierErrMsg = 'Sorry! You need to setup a supplier account to purchase this product.';
          if (err.data && err.data.message && err.data.message === supplierErrMsg) {
            this.data.buisness_builder_products.find(p => p._id.toString() === data._id.toString()).canBuy = false;
            this.$vs.notify({
              color: 'danger',
              title: 'Add to cart failed',
              text: supplierErrMsg,
              time: 5000,
            });
            return;
          }
          NOTIFICATIONS.PRODUCT_NOT_UPDATED(this.orderApprovalFlowEnabled);
        });
    },
    supplierActionLabel() {
      return this.item &&
        this.item.supplier &&
        this.item.supplier.supplierOnboardingLink
        ? 'Learn More'
        : 'Contact Fresh';
    },
    learnMore(url) {
      window.open(url);
    },
    popupActivate() {
      this.showPopup = true;
    },      
    handleLearnOrContact() {
      const link = this.item.supplier && this.item.supplier.supplierOnboardingLink;
      if (link) {
        this.learnMore(link);
      } else {
        this.popupActivate();
      }
    },
    isProductScript(item) {
      return item && item.type === 'script';
    },
    isProductBundle(item) {
      return item && item.type === 'bundle';
    },
  },
  async mounted() {
    const clinicId = sessionStorage.getItem('doctorClinicSelectedClinic');
    this.orderApprovalFlowEnabled = await this.$isClinicOrderApprovalFlowEnabled(clinicId);
    
    // Only change to "Add to Forecast" if order approval flow is enabled AND user is not a doctor
    if(this.orderApprovalFlowEnabled && !currentUserIsDoctor()) {
      this.addToCartTextTitle = 'Add to Forecast';
    }
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/vuexy/extraComponents/tree.scss";

.content-wrapper {
  background-color: white;
}

button.btn-async {
  background: rgba(var(--vs-warning), 0.15);
}

button.btn-delete {
  background: rgba(var(--vs-danger), 0.15);
}

div.bg-gray {
  background-color: #a0a0a0 !important;
}

#algolia-instant-search-demo {
  .algolia-header {
    .algolia-filters-label {
      width: calc(280px + 2.4rem);
    }
  }

  #algolia-content-container {
    .vs-sidebar {
      position: relative;
      float: left;
    }
  }

  .algolia-search-input-right-aligned-icon {
    padding: 1rem 1.5rem;
  }

  .algolia-price-slider {
    min-width: unset;
  }

  .item-view-primary-action-btn {
    color: #2c2c2c !important;
    background-color: #f6f6f6;
    min-width: 50%;
  }

  .item-view-secondary-action-btn {
    min-width: 50%;
  }
}

.theme-dark {
  #algolia-instant-search-demo {
    #algolia-content-container {
      .vs-sidebar {
        background-color: #10163a;
      }
    }
  }
}

@media (min-width: 992px) {
  .vs-sidebar-rounded {
    .vs-sidebar {
      border-radius: 0.5rem;
    }

    .vs-sidebar--items {
      border-radius: 0.5rem;
    }
  }

  .item-view-secondary-action-btn {
    // border-radius: 0 0 5px 0 !important;
  }
}

@media (max-width: 992px) {
  #algolia-content-container {
    .vs-sidebar {
      position: absolute !important;
      float: none !important;
    }
  }
}

.tree-list ul>li {
  margin-left: 20px;
  margin-bottom: 10px;
}

.tree-list li {
  margin-bottom: 10px;
}

.tree-list li ul {
  margin-top: 10px;
}

.add-to-cart-disabled {
  opacity: 0.5;
  cursor: default;
  pointer-events: none;
}

[dir] .con-select .vs-select--input {
  border: 0 !important;
  padding: 13px;
  font-size: 1.1rem;
}

.vs-select--options span {
  font-size: 1.1rem;
}


.items-wrapper {
  >div {
    border-left: 1px solid #eaeaea;
    border-bottom: 1px solid #eaeaea;

    &:first-of-type,
    &:nth-of-type(2) {
      border-top: 1px solid #eaeaea;
    }

    &:nth-of-type(even) {
      border-left: none;
      border-right: 1px solid #eaeaea;
    }

    &:nth-of-type(odd) {
      border-right: 1px solid #eaeaea;
    }
  }
}

@media (min-width: 992px) {
  .items-wrapper>div {
    border: 1px solid #eaeaea !important;
    border-radius: .3rem;
  }

}

@media (max-width: 991px) {
  .router-view {
    padding: 2rem 0 !important;
  }
}
</style>
